import dayjs, { type Dayjs } from 'dayjs';

import { resolveDateFormat } from './datePreference';

/**
 * Dates, in one place.
 *
 * The pattern is the company's, or the reader's own override of it, or `DD/MM/YYYY` when neither
 * has been set — see `format/datePreference.ts`, which is the only thing that answers that
 * question. Day-first is still the default, matching the `en_GB` locale the app is configured with:
 * for Indian users 01/02 is the first of February, and a screen that quietly renders it as January
 * the second is wrong in a way nobody notices until a financial year is mis-dated.
 *
 * The API speaks two shapes and they are not interchangeable. A `DateOnly` — a financial year's
 * start, a booking date — arrives as `YYYY-MM-DD` and has no time and no zone; converting it
 * through a `Date` in a zone behind UTC moves it to the previous day. A `DateTimeOffset` — an
 * audit stamp — is a real instant, sent UTC (rule 14) and shown in the reader's own zone, because
 * "last signed in at 04:30" is unhelpful to someone whose morning it was not.
 */

/**
 * The pattern in force, as a call rather than a constant.
 *
 * <p>
 * It was a constant until the format became settable, and the change is deliberate rather than
 * cosmetic: a module-level constant is read once at import and would pin every screen to whatever
 * was in force when the bundle loaded. Reading through here on each call is what lets a change on
 * the settings screen reach the forty-odd places that render a date.
 * </p>
 */
export function dateFormat(): string {
  return resolveDateFormat();
}

/**
 * The same pattern with a 24-hour clock after it.
 *
 * Derived rather than chosen separately: a shop picking how its dates read has not thereby said
 * anything about the clock, and two settings that can disagree is one more than this needs.
 */
export function dateTimeFormat(): string {
  return `${resolveDateFormat()} HH:mm`;
}

/** What a list cell shows in place of a value that is not there. */
export const absent = '—';

/** A `DateOnly` (`YYYY-MM-DD`) for display. Parsed as a plain date, never through a zone. */
export function formatDate(value: string | null | undefined): string {
  if (!value) {
    return absent;
  }

  // No explicit parse format: dayjs reads a bare `YYYY-MM-DD` as local midnight, which is what a
  // date without a time means. (A parse format would need the customParseFormat plugin, which is
  // not loaded — passing one anyway would look like it did something.)
  const parsed = dayjs(value.slice(0, 10));

  return parsed.isValid() ? parsed.format(dateFormat()) : absent;
}

/**
 * A `DateOnly` for a printed document — "Wed, 18 Nov 2026".
 *
 * <p>
 * Longer than {@link formatDate} on purpose. A slip goes home with a customer and comes back six
 * days later, and 18/11 versus 11/18 is exactly the ambiguity that produces someone arriving on the
 * wrong day. Naming the month removes it, and naming the weekday is what people actually plan
 * against — "back on Friday" is the sentence, not "back on the 20th".
 * </p>
 *
 * <p>
 * <strong>Deliberately outside the date-format setting</strong>, and that is the argument above
 * rather than an oversight. The setting decides how dates read to staff on a screen they can ask a
 * colleague about; this is the one date that leaves the shop, in the hand of somebody who cannot.
 * A company that picks `MM/DD/YYYY` for its own screens must not thereby start handing customers
 * a slip nobody can read unambiguously.
 * </p>
 */
export function formatDocumentDate(value: string | null | undefined): string {
  if (!value) {
    return absent;
  }

  const parsed = dayjs(value.slice(0, 10));

  return parsed.isValid() ? parsed.format('ddd, D MMM YYYY') : absent;
}

/** An instant, shown in the reader's own time zone. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return absent;
  }

  const parsed = dayjs(value);

  return parsed.isValid() ? parsed.format(dateTimeFormat()) : absent;
}

/**
 * A form value on its way to the API as a `DateOnly`.
 *
 * Ant Design's DatePicker hands back a Dayjs, ProForm's own date formatter sometimes hands back a
 * string, and either can arrive here — so both are accepted rather than assumed. Formatting the
 * Dayjs directly is what keeps the date the user picked: `toISOString` would convert to UTC first,
 * turning the 1st of April into the 31st of March for anyone east of Greenwich.
 */
export function toIsoDate(value: unknown): string | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  if (typeof value === 'string') {
    const parsed = dayjs(value);

    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
  }

  if (dayjs.isDayjs(value)) {
    return value.format('YYYY-MM-DD');
  }

  if (value instanceof Date) {
    return dayjs(value).format('YYYY-MM-DD');
  }

  return undefined;
}

/** A `DateOnly` from the API as the Dayjs a DatePicker needs for its initial value. */
export function toDayjs(value: string | null | undefined): Dayjs | undefined {
  if (!value) {
    return undefined;
  }

  // No explicit parse format: dayjs reads a bare `YYYY-MM-DD` as local midnight, which is what a
  // date without a time means. (A parse format would need the customParseFormat plugin, which is
  // not loaded — passing one anyway would look like it did something.)
  const parsed = dayjs(value.slice(0, 10));

  return parsed.isValid() ? parsed : undefined;
}

/**
 * The Indian statutory financial year containing a date: 1 April to 31 March, coded "2025-26".
 *
 * Offered as the default when a new year is created — it is the same answer every time, and
 * typing it by hand is how a year ends up starting on the 1st of March.
 */
export function suggestFinancialYear(on: Dayjs = dayjs()): {
  code: string;
  startDate: string;
  endDate: string;
} {
  // Jan–Mar belong to the year that started the previous April.
  const startYear = on.month() >= 3 ? on.year() : on.year() - 1;
  const start = dayjs(`${startYear}-04-01`);

  return {
    code: `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`,
    startDate: start.format('YYYY-MM-DD'),
    endDate: start.add(1, 'year').subtract(1, 'day').format('YYYY-MM-DD'),
  };
}
